<template>
  <b-modal ref="modalRef" centered title="Withdraw award" @hidden="reset">
    <p>
      Are you sure you want to withdraw the <strong>{{ formattedAmount }}</strong> award to
      <strong>{{ award?.nominee_name }}</strong
      >?
    </p>
    <p class="text-muted">
      The reward will be removed from their account and the funds returned to your rewards balance. This also removes
      the recognition message associated with the monetary award.
    </p>

    <b-form-group label="Reason (optional)" label-for="withdraw-reason-input" class="mb-0">
      <b-form-input
        id="withdraw-reason-input"
        v-model="reason"
        placeholder="e.g. Awarded by mistake"
        :maxlength="500"
      />
    </b-form-group>

    <template #modal-footer>
      <b-button variant="outline-secondary" :disabled="isSubmitting" @click="modalRef?.hide()">Cancel</b-button>
      <b-button variant="danger" :disabled="isSubmitting" @click="onConfirm">
        <b-spinner v-if="isSubmitting" small class="mr-1" />
        Withdraw award
      </b-button>
    </template>
  </b-modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { toCurrency } from '@/utils';
import useToast from '@/composables/useToast';
import { withdrawRecognitionGrant } from '@/queries/recognition';
import State from '@/state/State';

const props = defineProps({
  award: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['withdrawn']);

const { doToast, TOAST_VARIANT } = useToast();

const modalRef = ref(null);
const reason = ref('');

const withdrawMutation = useMutation(withdrawRecognitionGrant());
const isSubmitting = computed(() => withdrawMutation.isPending.value);

const currency = computed(() => State.state?.company?.default_currency);
const formattedAmount = computed(() => toCurrency(props.award?.amount_minor || 0, currency.value));

const ERROR_MESSAGES = {
  REDEEMED: 'This award has already been redeemed and can no longer be withdrawn.',
  PARTIALLY_REDEEMED: 'This award has already been partially redeemed and can no longer be withdrawn.',
  EXPIRED: 'This award has expired, so there is nothing to withdraw.',
  ALREADY_WITHDRAWN: 'This award has already been withdrawn.',
};

const onConfirm = async () => {
  try {
    await withdrawMutation.mutateAsync({
      grantId: props.award.recognition_grant_id,
      reason: reason.value.trim() || null,
    });
    doToast(
      'Success',
      `${formattedAmount.value} withdrawn and returned to your rewards balance`,
      TOAST_VARIANT.SUCCESS,
    );
    emit('withdrawn');
    modalRef.value?.hide();
  } catch (e) {
    const data = e?.response?.data || e?.variables || {};
    const message = ERROR_MESSAGES[data.error_code] || data.message || 'Failed to withdraw award. Please try again.';
    doToast('Error', message, TOAST_VARIANT.DANGER);
    modalRef.value?.hide();
  }
};

const show = () => {
  modalRef.value?.show();
};

const reset = () => {
  reason.value = '';
};

defineExpose({ show });
</script>
